import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Collapse, FormControlLabel, Stack, Switch, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined'
import { apiGet } from '../../generic/Api_Functions'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { getOperationPalette } from './operationLibraryApi'
import { CHOICE_KIND, NO_CHOICE, effectOf } from './libraryEffect'
import { NO_DESTINATION } from './miniTreeRows'
import { LIBRARY_FAMILY, effectiveLibraryFamily } from './processLibraryPanelPreferences'
import ProcessLibraryList from './ProcessLibraryList'
import OperationPaletteSection from './OperationPaletteSection'
import TechnologyLibraryList from './TechnologyLibraryList'
import { LibraryAccent, paletteColorOf } from './LibraryListParts'
import { SlidingMark, useSlidingSelection } from './SlidingSelection'

/**
 * LibraryFamilies — the library of the panel (story 167, HU-04, HU-05, HU-07).
 *
 * Three families, left to right, each with its own icon and colour (RN-06.1), its help text between the
 * buttons and the search (RN-06.4, D2, D11) and its own search box, kept while switching (RN-06.3). A family
 * is mounted the first time it is opened and stays mounted, hidden, afterwards.
 *
 * Operations exists only with OPERATION_LIBRARY (RN-06.2) — and so do the census, the "Pending to add"
 * filter and the counters (#152 RN-03.10): the gating is decided HERE, the single door the census comes in
 * through, as `NodeTypesBar.jsx:24-27` did.
 *
 * What the chosen piece will do when dropped is said at the bottom (RN-07.1), and while it is dragged, where
 * it will go (RN-07.4). The figures that text needs and nobody has asked yet — the version N6 offers for a
 * linked job of a process, the workstations of a technology — are asked here, once, and never invented.
 */
const FAMILY_LOOK = {
  [LIBRARY_FAMILY.PROCESSES]: {
    labelKey: 'Processes',
    helpKey: 'A whole process. When dropped it comes in as a subworkflow with all its operations and links.',
    Icon: AccountTreeOutlinedIcon,
    color: 'success.main'
  },
  [LIBRARY_FAMILY.OPERATIONS]: {
    labelKey: 'Operations',
    helpKey: 'A library operation. The node stays linked to its version.',
    Icon: HandymanOutlinedIcon,
    color: 'info.main'
  },
  [LIBRARY_FAMILY.TECHNOLOGIES]: {
    labelKey: 'Technologies',
    helpKey: 'A capability, not an operation. It creates an empty node that only sets the technology.',
    Icon: PrecisionManufacturingOutlinedIcon,
    color: 'warning.main'
  }
}

const NO_TICKET = { effect: { key: '', params: {} }, destination: NO_DESTINATION }

const FAMILY_ORDER = [LIBRARY_FAMILY.PROCESSES, LIBRARY_FAMILY.OPERATIONS, LIBRARY_FAMILY.TECHNOLOGIES]

const LibraryFamilies = ({
  visible = false,
  family = LIBRARY_FAMILY.PROCESSES,
  onFamilyChange = () => {},
  census = {},
  fetchTemplatePage,
  fetchTechnologyPage,
  dropDestination = NO_DESTINATION,
  dragActive = false,
  onItemDragStart = () => {},
  onItemDragEnd = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const canReadOperationLibrary = isUserAuthorized('OPERATION_LIBRARY')
  // RN-06.10: the duration shows with the permission that shows it in Tree; Sun does not filter it.
  const showDuration = isUserAuthorized('WORKFLOW_JOBS')
  const activeFamily = effectiveLibraryFamily(family, canReadOperationLibrary)
  const families = FAMILY_ORDER.filter((candidate) => candidate !== LIBRARY_FAMILY.OPERATIONS || canReadOperationLibrary)
  const [mountedFamilies, setMountedFamilies] = useState(() => new Set())
  const [pendingOnly, setPendingOnly] = useState(false)
  const [chosen, setChosen] = useState(NO_CHOICE)
  const [offeredVersionByCode, setOfferedVersionByCode] = useState({})
  const [workstationCountById, setWorkstationCountById] = useState({})
  const askedRef = useRef(new Set())

  const paletteCensus = canReadOperationLibrary ? census : undefined
  const pendingFilterOn = canReadOperationLibrary && pendingOnly

  useEffect(() => {
    if (visible) {
      setMountedFamilies((previous) => (previous.has(activeFamily) ? previous : new Set(previous).add(activeFamily)))
    }
  }, [visible, activeFamily])

  /** CA5.4: the version a linked job of a process will be linked to is the one N6 offers (#152 RN-01.5). */
  const askOfferedVersion = useCallback(async (operationCode) => {
    const askKey = 'operation:' + operationCode
    if (askedRef.current.has(askKey)) {
      return
    }
    askedRef.current.add(askKey)
    try {
      const offered = ((await getOperationPalette(operationCode)) || []).find((item) => item?.code === operationCode)
      if (offered) {
        setOfferedVersionByCode((current) => ({ ...current, [operationCode]: offered.versionNumber }))
      }
    } catch (thrown) {
      // RN-07.2 / CU6 A4: the text stays without the figure and without any alert; the next choice asks again.
      askedRef.current.delete(askKey)
    }
  }, [])

  /** CA5.3 / DL-14: the workstations of a technology, the list `NodeGeneralProperties.jsx:178` already asks. */
  const askWorkstationCount = useCallback(async (technologyId) => {
    const askKey = 'technology:' + technologyId
    if (askedRef.current.has(askKey)) {
      return
    }
    askedRef.current.add(askKey)
    try {
      const devices = await apiGet('devices/' + technologyId)
      if (Array.isArray(devices)) {
        setWorkstationCountById((current) => ({ ...current, [technologyId]: devices.length }))
      }
    } catch (thrown) {
      askedRef.current.delete(askKey)
    }
  }, [])

  const choose = useCallback((choice) => {
    setChosen(choice)
    if (choice.kind === CHOICE_KIND.PROCESS_LINKED_JOB) {
      askOfferedVersion(choice.code)
    }
    if (choice.kind === CHOICE_KIND.TECHNOLOGY) {
      askWorkstationCount(choice.id)
    }
  }, [askOfferedVersion, askWorkstationCount])

  /** CA5.5: starting to drag chooses too, so the text is there from the first moment of the drag. */
  const startItemDrag = useCallback((choice) => {
    choose(choice)
    onItemDragStart(choice)
  }, [choose, onItemDragStart])

  const [familiesRef, familyMark] = useSlidingSelection(activeFamily)
  const effect = effectOf(chosen, { offeredVersionByCode, workstationCountById, technologyLabel: (technology) => systemValueLabel(t_, 'technology', technology) })
  const destination = dragActive && dropDestination.key ? dropDestination : NO_DESTINATION
  const ticketShown = !!effect.key || !!destination.key
  // While the ticket folds away it keeps saying what it said: emptied first, it would shrink to nothing mid-fold.
  const lastTicketRef = useRef(NO_TICKET)
  if (ticketShown) {
    lastTicketRef.current = { effect, destination }
  }
  const ticket = ticketShown ? { effect, destination } : lastTicketRef.current
  const look = FAMILY_LOOK[activeFamily]
  const zoneStyle = (candidate) => ({ display: activeFamily === candidate ? 'flex' : 'none' })

  return (
    <Stack data-testid='library-families' sx={{ flex: '1 1 0', minHeight: 0 }}>
      <Stack spacing={0.75} sx={{ flex: '0 0 auto', p: 1, pb: 0.5 }}>
        {/* The raised pill slides to the family chosen (PO, 29/09/2026). */}
        <Box ref={familiesRef} sx={{ position: 'relative' }}>
          <SlidingMark box={familyMark} />
          <ToggleButtonGroup
            exclusive
            fullWidth
            size='small'
            value={activeFamily}
            onChange={(event, value) => {
              if (value) {
                onFamilyChange(value)
              }
            }}
            aria-label={t_('Library')}
          >
            {families.map((candidate) => {
              const candidateLook = FAMILY_LOOK[candidate]
              return (
                <ToggleButton
                  key={candidate}
                  value={candidate}
                  data-testid={'library-family-' + candidate}
                  data-color={candidateLook.color}
                  aria-label={t_(candidateLook.labelKey)}
                  sx={{ px: 0.5, minWidth: 0, position: 'relative', zIndex: 1, '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'transparent' } }}
                >
                  {/* Icon only, to leave the panel room to narrow; the tooltip says which family it is (PO, 28/09/2026). */}
                  <Tooltip title={t_(candidateLook.labelKey)}>
                    <Box component='span' sx={{ display: 'inline-flex' }}>
                      <candidateLook.Icon fontSize='small' sx={{ color: candidateLook.color }} />
                    </Box>
                  </Tooltip>
                </ToggleButton>
              )
            })}
          </ToggleButtonGroup>
        </Box>
        <Typography data-testid='library-family-help' variant='caption' color='text.secondary' sx={{ lineHeight: 1.35 }}>
          {t_(look.helpKey)}
        </Typography>
        {/* #152 S82 · CA7.2: the filter works on Processes (unfolded tree) and Operations at once. */}
        {canReadOperationLibrary && activeFamily !== LIBRARY_FAMILY.TECHNOLOGIES
          ? (
            <FormControlLabel
              data-testid='palette-pending-filter'
              control={<Switch size='small' checked={pendingOnly} onChange={(event) => setPendingOnly(event.target.checked)} />}
              label={<Typography variant='caption'>{t_('Pending to add')}</Typography>}
            />
            )
          : null}
      </Stack>
      <Box sx={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {mountedFamilies.has(LIBRARY_FAMILY.PROCESSES)
          ? (
            <Box data-testid='library-zone-PROCESSES' style={zoneStyle(LIBRARY_FAMILY.PROCESSES)} sx={{ flex: '1 1 0', minHeight: 0, flexDirection: 'column' }}>
              <LibraryAccent.Provider value={FAMILY_LOOK[LIBRARY_FAMILY.PROCESSES].color}>
                <ProcessLibraryList
                  active={visible && activeFamily === LIBRARY_FAMILY.PROCESSES}
                  showDuration={showDuration}
                  fetchTemplatePage={fetchTemplatePage}
                  canReadOperationLibrary={canReadOperationLibrary}
                  census={paletteCensus}
                  pendingOnly={pendingFilterOn}
                  chosenKey={chosen.key}
                  onChoose={choose}
                  onItemDragStart={startItemDrag}
                  onItemDragEnd={onItemDragEnd}
                />
              </LibraryAccent.Provider>
            </Box>
            )
          : null}
        {canReadOperationLibrary && mountedFamilies.has(LIBRARY_FAMILY.OPERATIONS)
          ? (
            <Box data-testid='library-zone-OPERATIONS' style={zoneStyle(LIBRARY_FAMILY.OPERATIONS)} sx={{ flex: '1 1 0', minHeight: 0, flexDirection: 'column' }}>
              <LibraryAccent.Provider value={FAMILY_LOOK[LIBRARY_FAMILY.OPERATIONS].color}>
                <OperationPaletteSection
                  active={visible && activeFamily === LIBRARY_FAMILY.OPERATIONS}
                  showDuration={showDuration}
                  census={paletteCensus}
                  pendingOnly={pendingFilterOn}
                  chosenKey={chosen.key}
                  onChoose={choose}
                  onItemDragStart={startItemDrag}
                  onItemDragEnd={onItemDragEnd}
                />
              </LibraryAccent.Provider>
            </Box>
            )
          : null}
        {mountedFamilies.has(LIBRARY_FAMILY.TECHNOLOGIES)
          ? (
            <Box data-testid='library-zone-TECHNOLOGIES' style={zoneStyle(LIBRARY_FAMILY.TECHNOLOGIES)} sx={{ flex: '1 1 0', minHeight: 0, flexDirection: 'column' }}>
              <LibraryAccent.Provider value={FAMILY_LOOK[LIBRARY_FAMILY.TECHNOLOGIES].color}>
                <TechnologyLibraryList
                  active={visible && activeFamily === LIBRARY_FAMILY.TECHNOLOGIES}
                  fetchTechnologyPage={fetchTechnologyPage}
                  chosenKey={chosen.key}
                  onChoose={choose}
                  onItemDragStart={startItemDrag}
                  onItemDragEnd={onItemDragEnd}
                />
              </LibraryAccent.Provider>
            </Box>
            )
          : null}
      </Box>
      {/* What the drop will do is the one loud thing of the panel: a dashed ticket in the colour of the family it
          speaks for (PO, 29/09/2026: no coloured bar on the left). It takes no room while
          nothing is chosen and unfolds when something is; folding back, it keeps its last words until it is closed. */}
      <Collapse in={ticketShown} timeout={220} sx={{ flex: '0 0 auto' }}>
        <Box
          data-testid='library-effect'
          sx={(theme) => {
            const color = paletteColorOf(theme, FAMILY_LOOK[activeFamily].color)
            return {
              mx: 1,
              mb: 1,
              mt: 0.5,
              px: 1.25,
              py: 0.75,
              borderRadius: '10px',
              border: '1px dashed',
              borderColor: alpha(color, 0.4),
              bgcolor: alpha(color, 0.06),
              transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], { duration: theme.transitions.duration.shortest })
            }
          }}
        >
          <Typography
            component='div'
            sx={(theme) => ({ fontSize: 10, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: paletteColorOf(theme, FAMILY_LOOK[activeFamily].color), mb: 0.25 })}
          >
            {t_('When dropped')}
          </Typography>
          {ticket.effect.key ? <Typography data-testid='library-effect-text' variant='body2' component='div' sx={{ fontSize: 13, lineHeight: 1.35 }}>{t_(ticket.effect.key, ticket.effect.params)}</Typography> : null}
          {ticket.destination.key
            ? <Typography data-testid='library-effect-destination' variant='caption' component='div' color='primary.main' sx={{ fontWeight: 600, mt: 0.25 }}>{t_(ticket.destination.key, ticket.destination.params)}</Typography>
            : null}
        </Box>
      </Collapse>
    </Stack>
  )
}

export default React.memo(LibraryFamilies)
